import React, { useState, useEffect } from 'react';
import { 
  Database, 
  BarChart3, 
  TrendingUp, 
  Activity, 
  AlertCircle, 
  CheckCircle, 
  Clock, 
  Users, 
  Settings,
  Download,
  Search,
  Filter,
  RefreshCw,
  Eye,
  Calendar,
  Target,
  Zap,
  Shield,
  Gauge
} from 'lucide-react';
import esgDataOverviewService from '../../../services/esg/esgDataOverviewService';

/**
 * ESG Data Overview Component
 * 
 * Comprehensive data analytics and intelligence dashboard for ESG data management.
 * Provides multi-dimensional data visualization, KPI tracking, real-time analytics,
 * and intelligent insights for enterprise ESG data ecosystem.
 */
export default function ESGDataOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('executive');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const unsubscribe = esgDataOverviewService.subscribe((newData) => {
      setData(newData);
      setLoading(false);
    });

    // Initial data load
    setData({
      executiveDashboard: esgDataOverviewService.getExecutiveDashboard(),
      dataSources: esgDataOverviewService.getDataSources(),
      kpiDashboard: esgDataOverviewService.getKPIDashboard(),
      realTimeAnalytics: esgDataOverviewService.getRealTimeAnalytics(),
      dataQuality: esgDataOverviewService.getDataQualityMetrics(),
      reporting: esgDataOverviewService.getReportingStatus(),
      visualizations: esgDataOverviewService.getVisualizations(),
      analytics: esgDataOverviewService.getAnalyticsInsights(),
      systemPerformance: esgDataOverviewService.getSystemPerformance(),
      userEngagement: esgDataOverviewService.getUserEngagement()
    });
    setLoading(false);

    return unsubscribe;
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    // Simulate data refresh
    setTimeout(() => {
      setRefreshing(false);
      // In real implementation, would trigger data refresh
    }, 2000);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.trim()) {
      const results = esgDataOverviewService.searchData(query, filterCategory === 'all' ? null : filterCategory);
      console.log('Search results:', results);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  const getStatusColor = (status) => {
    const colors = {
      'Active': 'text-green-600 bg-green-50',
      'Connected': 'text-blue-600 bg-blue-50',
      'Excellent': 'text-green-600 bg-green-50',
      'Good': 'text-emerald-600 bg-emerald-50',
      'Normal': 'text-gray-600 bg-gray-50',
      'Compliant': 'text-green-600 bg-green-50',
      'Partially Compliant': 'text-yellow-600 bg-yellow-50',
      'In Progress': 'text-blue-600 bg-blue-50',
      'Scheduled': 'text-purple-600 bg-purple-50'
    };
    return colors[status] || 'text-gray-600 bg-gray-50';
  };

  const getTrendIcon = (trend) => {
    if (trend === 'Increasing' || trend === 'Improving') {
      return <TrendingUp className="w-4 h-4 text-green-600" />;
    } else if (trend === 'Decreasing') {
      return <TrendingUp className="w-4 h-4 text-red-600 rotate-180" />;
    }
    return <Activity className="w-4 h-4 text-gray-600" />;
  };

  if (loading) {
    return (
      <div className="h-96 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading ESG data analytics...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'executive', label: 'Executive Dashboard', icon: <BarChart3 className="w-5 h-5" /> },
    { id: 'sources', label: 'Data Sources', icon: <Database className="w-5 h-5" /> },
    { id: 'kpis', label: 'KPI Dashboard', icon: <Target className="w-5 h-5" /> },
    { id: 'analytics', label: 'Real-time Analytics', icon: <Activity className="w-5 h-5" /> },
    { id: 'quality', label: 'Data Quality', icon: <Shield className="w-5 h-5" /> },
    { id: 'reports', label: 'Reporting', icon: <Calendar className="w-5 h-5" /> },
    { id: 'visualizations', label: 'Visualizations', icon: <Eye className="w-5 h-5" /> },
    { id: 'insights', label: 'Intelligence', icon: <Zap className="w-5 h-5" /> }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">ESG Data Overview</h1>
            <p className="text-gray-600 mt-1">Comprehensive analytics and intelligence platform for ESG data management</p>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search ESG data, KPIs, insights..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div className="relative">
            <Filter className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">All Categories</option>
              <option value="environmental">Environmental</option>
              <option value="social">Social</option>
              <option value="governance">Governance</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-shrink-0 flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Executive Dashboard Tab */}
          {activeTab === 'executive' && data?.executiveDashboard && (
            <div className="space-y-6">
              {/* Overview Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-6 border border-purple-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-600 text-sm font-medium">Total Data Points</p>
                      <p className="text-2xl font-bold text-purple-900">{formatNumber(data.executiveDashboard.overview.totalDataPoints)}</p>
                    </div>
                    <Database className="w-8 h-8 text-purple-600" />
                  </div>
                  <p className="text-purple-700 text-sm mt-2">Across {data.executiveDashboard.overview.activeDataStreams} active streams</p>
                </div>

                <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-lg p-6 border border-emerald-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-emerald-600 text-sm font-medium">Data Quality Score</p>
                      <p className="text-2xl font-bold text-emerald-900">{data.executiveDashboard.overview.dataQualityScore}%</p>
                    </div>
                    <Shield className="w-8 h-8 text-emerald-600" />
                  </div>
                  <p className="text-emerald-700 text-sm mt-2">{data.executiveDashboard.overview.systemHealth} performance</p>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-600 text-sm font-medium">Overall ESG Score</p>
                      <p className="text-2xl font-bold text-blue-900">{data.executiveDashboard.kpiSummary.overallESGScore}%</p>
                    </div>
                    <Gauge className="w-8 h-8 text-blue-600" />
                  </div>
                  <div className="flex items-center mt-2">
                    <TrendingUp className="w-4 h-4 text-green-600 mr-1" />
                    <p className="text-blue-700 text-sm">+{data.executiveDashboard.kpiSummary.monthlyGrowth}% this month</p>
                  </div>
                </div>
              </div>

              {/* ESG Scores Breakdown */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">ESG Performance Breakdown</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="w-20 h-20 mx-auto mb-3 relative">
                      <svg className="w-20 h-20 transform -rotate-90">
                        <circle cx="40" cy="40" r="32" stroke="#e5e7eb" strokeWidth="8" fill="none" />
                        <circle 
                          cx="40" 
                          cy="40" 
                          r="32" 
                          stroke="#059669" 
                          strokeWidth="8" 
                          fill="none"
                          strokeDasharray={`${(data.executiveDashboard.kpiSummary.environmentalScore / 100) * 201} 201`}
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-lg font-bold text-emerald-600">{data.executiveDashboard.kpiSummary.environmentalScore}</span>
                      </div>
                    </div>
                    <p className="font-medium text-gray-900">Environmental</p>
                    <p className="text-sm text-gray-600">Carbon, Energy, Waste</p>
                  </div>

                  <div className="text-center">
                    <div className="w-20 h-20 mx-auto mb-3 relative">
                      <svg className="w-20 h-20 transform -rotate-90">
                        <circle cx="40" cy="40" r="32" stroke="#e5e7eb" strokeWidth="8" fill="none" />
                        <circle 
                          cx="40" 
                          cy="40" 
                          r="32" 
                          stroke="#3b82f6" 
                          strokeWidth="8" 
                          fill="none"
                          strokeDasharray={`${(data.executiveDashboard.kpiSummary.socialScore / 100) * 201} 201`}
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-lg font-bold text-blue-600">{data.executiveDashboard.kpiSummary.socialScore}</span>
                      </div>
                    </div>
                    <p className="font-medium text-gray-900">Social</p>
                    <p className="text-sm text-gray-600">People, Community, Safety</p>
                  </div>

                  <div className="text-center">
                    <div className="w-20 h-20 mx-auto mb-3 relative">
                      <svg className="w-20 h-20 transform -rotate-90">
                        <circle cx="40" cy="40" r="32" stroke="#e5e7eb" strokeWidth="8" fill="none" />
                        <circle 
                          cx="40" 
                          cy="40" 
                          r="32" 
                          stroke="#8b5cf6" 
                          strokeWidth="8" 
                          fill="none"
                          strokeDasharray={`${(data.executiveDashboard.kpiSummary.governanceScore / 100) * 201} 201`}
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-lg font-bold text-purple-600">{data.executiveDashboard.kpiSummary.governanceScore}</span>
                      </div>
                    </div>
                    <p className="font-medium text-gray-900">Governance</p>
                    <p className="text-sm text-gray-600">Ethics, Risk, Compliance</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Data Sources Tab */}
          {activeTab === 'sources' && data?.dataSources && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Data Sources Overview</h3>
                <button className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                  Add Source
                </button>
              </div>

              {/* Internal Systems */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-4">Internal Systems</h4>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {data.dataSources.internalSystems.map((source) => (
                    <div key={source.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h5 className="font-medium text-gray-900">{source.name}</h5>
                          <p className="text-sm text-gray-600">{source.type}</p>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(source.status)}`}>
                          {source.status}
                        </span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Data Points:</span>
                          <span className="font-medium">{formatNumber(source.dataPoints)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Freshness:</span>
                          <span className="font-medium">{source.freshness}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Quality Score:</span>
                          <span className="font-medium">{source.qualityScore}%</span>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {source.categories.map((category) => (
                            <span key={category} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                              {category}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* External Sources */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-4">External Sources</h4>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {data.dataSources.externalSources.map((source) => (
                    <div key={source.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h5 className="font-medium text-gray-900">{source.name}</h5>
                          <p className="text-sm text-gray-600">{source.provider}</p>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(source.status)}`}>
                          {source.status}
                        </span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Data Points:</span>
                          <span className="font-medium">{formatNumber(source.dataPoints)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Update Frequency:</span>
                          <span className="font-medium">{source.updateFrequency}</span>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {source.categories.map((category) => (
                            <span key={category} className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                              {category}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* KPI Dashboard Tab */}
          {activeTab === 'kpis' && data?.kpiDashboard && (
            <div className="space-y-6">
              {Object.entries(data.kpiDashboard).map(([category, metrics]) => (
                <div key={category} className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 capitalize">{category} KPIs</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(metrics).map(([metric, data]) => (
                      <div key={metric} className="bg-white rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-gray-900 capitalize text-sm">{metric.replace(/([A-Z])/g, ' $1').trim()}</h4>
                          {getTrendIcon(data.trend)}
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-end gap-2">
                            <span className="text-2xl font-bold text-gray-900">{formatNumber(data.value)}</span>
                            <span className="text-sm text-gray-600">{data.unit}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${data.progress >= 90 ? 'bg-green-500' : data.progress >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`}
                              style={{ width: `${Math.min(100, data.progress)}%` }}
                            ></div>
                          </div>
                          <div className="flex justify-between text-xs text-gray-600">
                            <span>Target: {formatNumber(data.target)} {data.unit}</span>
                            <span>{data.progress}%</span>
                          </div>
                          {data.monthlyChange && (
                            <div className={`text-xs ${data.monthlyChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {data.monthlyChange >= 0 ? '+' : ''}{data.monthlyChange}% this month
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Real-time Analytics Tab */}
          {activeTab === 'analytics' && data?.realTimeAnalytics && (
            <div className="space-y-6">
              {/* Live Data Streams */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Live Data Streams</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {data.realTimeAnalytics.dataStreams.map((stream) => (
                    <div key={stream.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900">{stream.name}</h4>
                        <span className={`w-3 h-3 rounded-full ${stream.status === 'Active' ? 'bg-green-500' : 'bg-red-500'}`}></span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-end gap-2">
                          <span className="text-xl font-bold text-gray-900">{stream.currentValue}</span>
                          <span className="text-sm text-gray-600">{stream.unit}</span>
                        </div>
                        <div className="text-sm text-gray-600">Type: {stream.type}</div>
                        <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                          stream.alertLevel === 'Normal' ? 'bg-green-100 text-green-700' :
                          stream.alertLevel === 'Good' ? 'bg-blue-100 text-blue-700' :
                          stream.alertLevel === 'Low' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {stream.alertLevel}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Active Alerts */}
              {data.realTimeAnalytics.alerts.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Active Alerts</h3>
                  <div className="space-y-3">
                    {data.realTimeAnalytics.alerts.map((alert) => (
                      <div key={alert.id} className={`border rounded-lg p-4 ${
                        alert.severity === 'High' ? 'border-red-200 bg-red-50' :
                        alert.severity === 'Medium' ? 'border-yellow-200 bg-yellow-50' :
                        'border-blue-200 bg-blue-50'
                      }`}>
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <AlertCircle className={`w-5 h-5 mt-0.5 ${
                              alert.severity === 'High' ? 'text-red-600' :
                              alert.severity === 'Medium' ? 'text-yellow-600' :
                              'text-blue-600'
                            }`} />
                            <div>
                              <h4 className="font-medium text-gray-900">{alert.type}</h4>
                              <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
                              <p className="text-xs text-gray-500 mt-2">
                                {new Date(alert.timestamp).toLocaleString()}
                              </p>
                            </div>
                          </div>
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(alert.status)}`}>
                            {alert.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Data Quality Tab */}
          {activeTab === 'quality' && data?.dataQuality && (
            <div className="space-y-6">
              {/* Overall Quality Metrics */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Overall Data Quality</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {Object.entries(data.dataQuality.overall).map(([metric, value]) => (
                    <div key={metric} className="text-center">
                      <div className="text-2xl font-bold text-gray-900">{value}%</div>
                      <div className="text-sm text-gray-600 capitalize">{metric}</div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                        <div 
                          className={`h-2 rounded-full ${value >= 95 ? 'bg-green-500' : value >= 85 ? 'bg-yellow-500' : 'bg-red-500'}`}
                          style={{ width: `${value}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quality by Source */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quality by Source</h3>
                <div className="space-y-4">
                  {data.dataQuality.bySource.map((source) => (
                    <div key={source.source} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900">{source.source}</h4>
                        <span className="text-lg font-bold text-gray-900">{source.overall}%</span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="text-gray-600">Completeness</div>
                          <div className="font-medium">{source.completeness}%</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Accuracy</div>
                          <div className="font-medium">{source.accuracy}%</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Consistency</div>
                          <div className="font-medium">{source.consistency}%</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Timeliness</div>
                          <div className="font-medium">{source.timeliness}%</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Reporting Tab */}
          {activeTab === 'reports' && data?.reporting && (
            <div className="space-y-6">
              {/* Scheduled Reports */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Scheduled Reports</h3>
                <div className="space-y-4">
                  {data.reporting.scheduledReports.map((report) => (
                    <div key={report.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-medium text-gray-900">{report.name}</h4>
                          <p className="text-sm text-gray-600">{report.type} • {report.frequency}</p>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(report.status)}`}>
                          {report.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <div className="text-gray-600">Last Generated</div>
                          <div className="font-medium">{new Date(report.lastGenerated).toLocaleDateString()}</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Next Scheduled</div>
                          <div className="font-medium">{new Date(report.nextScheduled).toLocaleDateString()}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Compliance Frameworks */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Compliance Frameworks</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {data.reporting.complianceFrameworks.map((framework) => (
                    <div key={framework.name} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900">{framework.name}</h4>
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(framework.status)}`}>
                          {framework.status}
                        </span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Coverage:</span>
                          <span className="font-medium">{framework.coverage}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${framework.coverage >= 90 ? 'bg-green-500' : framework.coverage >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`}
                            style={{ width: `${framework.coverage}%` }}
                          ></div>
                        </div>
                        <div className="text-xs text-gray-600">
                          Next Review: {new Date(framework.nextReview).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Visualizations Tab */}
          {activeTab === 'visualizations' && data?.visualizations && (
            <div className="space-y-6">
              {/* Dashboards */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Dashboards</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {data.visualizations.dashboards.map((dashboard) => (
                    <div key={dashboard.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-medium text-gray-900">{dashboard.name}</h4>
                          <p className="text-sm text-gray-600">{dashboard.type}</p>
                        </div>
                        {dashboard.favorite && (
                          <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <div className="text-gray-600">Widgets:</div>
                          <div className="font-medium">{dashboard.widgets}</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Viewers:</div>
                          <div className="font-medium">{dashboard.viewers}</div>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mt-2">
                        Updated: {new Date(dashboard.lastUpdated).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Chart Types Usage */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Chart Types Usage</h3>
                <div className="space-y-3">
                  {data.visualizations.chartTypes.map((chart) => (
                    <div key={chart.type} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                      <div>
                        <span className="font-medium text-gray-900">{chart.type}</span>
                        <span className="text-sm text-gray-600 ml-2">({chart.count} charts)</span>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        chart.usage === 'Very High' ? 'bg-green-100 text-green-700' :
                        chart.usage === 'High' ? 'bg-blue-100 text-blue-700' :
                        chart.usage === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {chart.usage}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Intelligence Tab */}
          {activeTab === 'insights' && data?.analytics && (
            <div className="space-y-6">
              {/* Predictive Models */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Predictive Models</h3>
                <div className="space-y-4">
                  {data.analytics.predictiveModels.map((model) => (
                    <div key={model.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-medium text-gray-900">{model.name}</h4>
                          <p className="text-sm text-gray-600">{model.type}</p>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(model.status)}`}>
                          {model.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <div className="text-gray-600">Accuracy:</div>
                          <div className="font-medium">{model.accuracy}%</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Last Trained:</div>
                          <div className="font-medium">{new Date(model.lastTrained).toLocaleDateString()}</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Prediction:</div>
                          <div className="font-medium">{model.predictions}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* AI Insights */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">AI-Generated Insights</h3>
                <div className="space-y-4">
                  {data.analytics.insights.map((insight) => (
                    <div key={insight.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-medium text-gray-900">{insight.title}</h4>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              insight.impact === 'High' ? 'bg-red-100 text-red-700' :
                              insight.impact === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {insight.impact} Impact
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-3">{insight.description}</p>
                          <div className="text-xs text-gray-500 mb-3">
                            Confidence: {insight.confidence}% • Category: {insight.category}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900 mb-2">Action Items:</div>
                            <ul className="text-sm text-gray-600 space-y-1">
                              {insight.actionItems.map((item, index) => (
                                <li key={index} className="flex items-center gap-2">
                                  <CheckCircle className="w-3 h-3 text-green-600" />
                                  {item}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}